import classNames from "classnames";
import * as React from "react";

import {staticUrl} from "@khan/urls";

import {
    getAvatarDisplayName,
    getAvatarStyleDescription,
} from "#profile-nav-package/strings.ts";
import AphroditeCSSTransitionGroup from "#shared-styles-package/aphrodite-css-transition-group.tsx";
import {createTransition} from "#shared-styles-package/util.ts";

import {AVATAR_TRANSITION_DURATION_MS} from "./avatar-customizer.tsx";
import type {AvatarModel, AvatarPart, AvatarsAnimation} from "./types.ts";

type Props = {
    animation: AvatarsAnimation;
    avatarModel: AvatarModel;
    avatarStyle: AvatarPart;
};

/**
 * Dynamically generates a preview of the user's avatar in the customizer.
 */
export default class AvatarPreview extends React.Component<Props> {
    render(): React.ReactNode {
        const model = this.props.avatarModel;
        const style = this.props.avatarStyle;
        const animationKey = `${model.name}/${style.name}`;

        // Style requirements overrule the base model requirements, unless
        // the style itself has no requirements.
        const requirements =
            style.requirements.join(";") || model.requirements.join(";");
        const isAvailable = style.isAvailable && model.isAvailable;

        const className = classNames({
            "avatar-preview": true,
            available: isAvailable,
            "not-available": !isAvailable,
        });

        const getTransitionStyles = () => {
            if (this.props.animation === "slide-left") {
                return {
                    enter: [
                        avatarDefaultTransition.enter,
                        avatarSlideLeftTransition.enter,
                    ],
                    enterActive: [
                        avatarDefaultTransition.enterActive,
                        avatarSlideLeftTransition.enterActive,
                    ],
                    leave: [
                        avatarDefaultTransition.leave,
                        avatarSlideLeftTransition.leave,
                    ],
                    leaveActive: [
                        avatarDefaultTransition.leaveActive,
                        avatarSlideLeftTransition.leaveActive,
                    ],
                };
            }
            if (this.props.animation === "slide-right") {
                return {
                    enter: [
                        avatarDefaultTransition.enter,
                        avatarSlideRightTransition.enter,
                    ],
                    enterActive: [
                        avatarDefaultTransition.enterActive,
                        avatarSlideRightTransition.enterActive,
                    ],
                    leave: [
                        avatarDefaultTransition.leave,
                        avatarSlideRightTransition.leave,
                    ],
                    leaveActive: [
                        avatarDefaultTransition.leaveActive,
                        avatarSlideRightTransition.leaveActive,
                    ],
                };
            }
            return avatarDefaultTransition;
        };

        return (
            <div className={`${className} ${this.props.animation}`}>
                <AphroditeCSSTransitionGroup
                    transitionStyle={getTransitionStyles}
                    transitionEnterTimeout={AVATAR_TRANSITION_DURATION_MS}
                    transitionLeaveTimeout={AVATAR_TRANSITION_DURATION_MS}
                >
                    <div className="avatar-transition-group" key={animationKey}>
                        {!isAvailable && (
                            <i className="icon-lock locked-part-icon" />
                        )}
                        <img
                            className="avatar-preview-style"
                            src={staticUrl(style.imageSrc)}
                            alt={getAvatarStyleDescription(style.name)}
                        />
                        <div className="avatar-model-name">
                            {getAvatarDisplayName(model.name)}
                        </div>
                        {requirements && (
                            <div className="avatar-model-requirements">
                                {isAvailable ? (
                                    <i className="requirement-icon icon-ok" />
                                ) : (
                                    <i className="requirement-icon icon-lock" />
                                )}
                                {requirements}
                            </div>
                        )}
                    </div>
                </AphroditeCSSTransitionGroup>
            </div>
        );
    }
}

const avatarDefaultTransition = createTransition({
    enter: {
        opacity: 0,
    },
    enterActive: {
        opacity: 1,
    },
    leave: {
        opacity: 1,
    },
    leaveActive: {
        opacity: 0,
    },
});

const avatarSlideRightTransition = createTransition({
    enter: {
        left: -500,
    },
    enterActive: {
        left: 0,
    },
    leave: {
        left: 0,
    },
    leaveActive: {
        left: 500,
    },
});

const avatarSlideLeftTransition = createTransition({
    enter: {
        left: 500,
    },
    enterActive: {
        left: 0,
    },
    leave: {
        left: 0,
    },
    leaveActive: {
        left: -500,
    },
});
